import { useMemo, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoCard from "@/components/feature/VideoCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  getCategoryById,
  getVideosByCategory,
  getTagPool,
  sortVideos,
  filterByTime,
  type SortKey,
  type TimeRange,
} from "@/lib/videoData";
import NotFound from "@/pages/NotFound";

const PAGE_SIZE = 8;
const MORE_STEP = 8;

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: "hot", label: "最热门", icon: "ri-fire-line" },
  { key: "new", label: "最新发布", icon: "ri-time-line" },
  { key: "likes", label: "最多点赞", icon: "ri-thumb-up-line" },
];

const timeOptions: { key: TimeRange; label: string }[] = [
  { key: "all", label: "全部时间" },
  { key: "week", label: "近一周" },
  { key: "month", label: "近一月" },
];

export default function CategoryPage() {
  const { categoryId } = useParams();
  const category = getCategoryById(categoryId);

  const [tags, setTags] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>("hot");
  const [time, setTime] = useState<TimeRange>("all");
  const [page, setPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [mode, setMode] = useState<"page" | "more">("page");
  const [filterOpen, setFilterOpen] = useState(false);

  usePageMeta({
    title: `${category ? category.name : "分类"} - 快手短视频平台`,
    description: category
      ? `${category.desc} 浏览快手${category.name}频道的热门短视频，发现更多精彩内容。`
      : "浏览快手各分类频道的热门短视频内容。",
    keywords: category ? `快手,${category.name},短视频,${category.name}短视频` : "快手,短视频,分类",
  });

  const tagPool = useMemo(() => (categoryId ? getTagPool(categoryId) : []), [categoryId]);

  const filtered = useMemo(() => {
    if (!categoryId) return [];
    let list = getVideosByCategory(categoryId);
    if (tags.length > 0) {
      list = list.filter((v) => v.tags.some((t) => tags.includes(t)));
    }
    list = filterByTime(list, time);
    return sortVideos(list, sort);
  }, [categoryId, tags, time, sort]);

  useEffect(() => {
    setPage(1);
    setVisibleCount(PAGE_SIZE);
  }, [categoryId, tags, time, sort]);

  if (!category) {
    return <NotFound />;
  }

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedList =
    mode === "page"
      ? filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
      : filtered.slice(0, visibleCount);

  const toggleTag = (tag: string) => {
    setTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const resetFilters = () => {
    setTags([]);
    setTime("all");
    setSort("hot");
  };

  const hasActiveFilter = tags.length > 0 || time !== "all" || sort !== "hot";

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 pt-16">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5">
            <Link to="/" className="hover:text-primary-600 transition-colors flex items-center gap-1">
              <i className="ri-home-4-line"></i>
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-800 font-medium">{category.name}</span>
          </nav>

          <section className="rounded-xl bg-gradient-to-br from-primary-50 to-background-100 border border-background-200 px-6 py-7 md:px-8 md:py-8 mb-6">
            <div className="flex items-center gap-4">
              <span className="w-14 h-14 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center text-3xl shrink-0">
                <i className={category.icon}></i>
              </span>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.desc}</p>
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground-800">
                <i className="ri-filter-3-line text-primary-500"></i>
                筛选与排序
                {hasActiveFilter && (
                  <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs">
                    已筛选
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  重置
                </button>
                <button
                  type="button"
                  onClick={() => setFilterOpen((v) => !v)}
                  className="md:hidden text-sm text-primary-600 flex items-center gap-1 cursor-pointer"
                >
                  {filterOpen ? "收起" : "展开"}
                  <i className={`ri-arrow-down-s-line transition-transform ${filterOpen ? "rotate-180" : ""}`}></i>
                </button>
              </div>
            </div>

            <div className={`${filterOpen ? "block" : "hidden"} md:block mt-4 space-y-4`}>
              <div className="flex flex-col md:flex-row md:items-start gap-2 md:gap-4">
                <span className="text-sm text-foreground-400 shrink-0 md:w-16 md:pt-1.5">标签</span>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setTags([])}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer ${
                      tags.length === 0
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    全部
                  </button>
                  {tagPool.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer ${
                        tags.includes(tag)
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-600 hover:bg-background-200"
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <span className="text-sm text-foreground-400 shrink-0 md:w-16">发布时间</span>
                <div className="flex flex-wrap gap-2">
                  {timeOptions.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setTime(t.key)}
                      className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer ${
                        time === t.key
                          ? "bg-accent-500 text-background-50"
                          : "bg-background-100 text-foreground-600 hover:bg-background-200"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <span className="text-sm text-foreground-400 shrink-0 md:w-16">热门排序</span>
                <div className="flex flex-wrap gap-2">
                  {sortOptions.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setSort(s.key)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer ${
                        sort === s.key
                          ? "bg-secondary-500 text-background-50"
                          : "bg-background-100 text-foreground-600 hover:bg-background-200"
                      }`}
                    >
                      <i className={s.icon}></i>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-foreground-500">
              共 <span className="text-primary-600 font-medium">{filtered.length}</span> 个视频
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              <button
                type="button"
                onClick={() => setMode("page")}
                className={`px-3 py-1 rounded-full text-xs transition-colors cursor-pointer ${
                  mode === "page" ? "bg-background-50 text-primary-600" : "text-foreground-500"
                }`}
              >
                翻页模式
              </button>
              <button
                type="button"
                onClick={() => setMode("more")}
                className={`px-3 py-1 rounded-full text-xs transition-colors cursor-pointer ${
                  mode === "more" ? "bg-background-50 text-primary-600" : "text-foreground-500"
                }`}
              >
                加载更多
              </button>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-3xl text-foreground-400 mb-4">
                <i className="ri-inbox-line"></i>
              </span>
              <p className="text-lg font-medium text-foreground-800">没有找到匹配的视频</p>
              <p className="text-sm text-foreground-500 mt-1 mb-5">试试调整筛选条件，或重置后重新浏览</p>
              <button
                type="button"
                onClick={resetFilters}
                className="h-10 px-5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                重置筛选
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-product-shop>
                {pagedList.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>

              {mode === "page" ? (
                totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPage(i + 1)}
                        className={`w-9 h-9 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                          page === i + 1
                            ? "bg-primary-500 text-background-50"
                            : "border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )
              ) : (
                visibleCount < filtered.length && (
                  <div className="flex justify-center mt-8">
                    <button
                      type="button"
                      onClick={() => setVisibleCount((c) => c + MORE_STEP)}
                      className="flex items-center gap-2 h-11 px-6 rounded-full border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                      加载更多视频
                    </button>
                  </div>
                )
              )}
            </>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}